import * as v from 'valibot';
import { createEnv } from 'valibot-env/nextjs';
import { NEXT_PUBLIC_ABSMARTLY_URL, NEXT_PUBLIC_SUPERGRAPH_URL } from './constants';
import { deployableEnvSchema } from './schema';

export const env = createEnv({
  schema: {
    public: {
      NEXT_PUBLIC_ABSMARTLY_API_KEY: v.fallback(v.string(), ''),
      NEXT_PUBLIC_ABSMARTLY_URL: v.fallback(v.string(), ''),
      NEXT_PUBLIC_DD_CLIENT_TOKEN: v.optional(v.string()),
      NEXT_PUBLIC_DEPLOYABLE_ENV: deployableEnvSchema,
      NEXT_PUBLIC_NW_URL: v.fallback(
        v.pipe(v.string(), v.url('Must be a valid URL')),
        'https://www.nerdwallet.com/',
      ),
      NEXT_PUBLIC_SEGMENT_WRITE_KEY: v.fallback(v.string(), ''),
      NEXT_PUBLIC_SUPERGRAPH_URL: v.pipe(
        v.string(),
        v.url('NEXT_PUBLIC_SUPERGRAPH_URL must be a valid URL'),
      ),
      NEXT_PUBLIC_VERCEL_DEPLOYMENT_ID: v.fallback(v.string(), ''),
      NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: v.fallback(v.string(), ''),
    },
    private: {
      ASSET_PREFIX: v.fallback(v.string(), ''),
      ACXIOM_CLIENT_ID: v.pipe(
        v.optional(v.string()),
        v.transform(input => (input === undefined ? '' : input)),
        v.string('ACXIOM_CLIENT_ID must be a string'),
      ),
      ACXIOM_CLIENT_SECRET: v.pipe(
        v.optional(v.string()),
        v.transform(input => (input === undefined ? '' : input)),
        v.string('ACXIOM_CLIENT_SECRET must be a string'),
      ),
      AWS_ACXIOM_SQS_QUEUE_URL: v.pipe(
        v.optional(v.string()),
        v.custom(input => {
          const url = input as string | undefined;
          const shouldRequireQueueUrl = process.env.AWS_ACXIOM_SQS_ACTIVE;
          if (shouldRequireQueueUrl === '1') {
            const result = v.safeParse(v.pipe(v.string(), v.nonEmpty(), v.url()), url);
            return result.success;
          }

          if (url === undefined || url === '') {
            return true;
          }
          return v.safeParse(v.pipe(v.string(), v.nonEmpty(), v.url()), url).success;
        }, 'AWS_ACXIOM_SQS_QUEUE_URL is required and must be a valid URL when AWS_ACXIOM_SQS_ACTIVE is true.'),
      ),
      AWS_ACXIOM_SQS_ACTIVE: v.pipe(
        v.optional(v.string()),
        v.transform(value => {
          return value === undefined || value === '' ? 'IGNORE' : value;
        }),
        v.transform(value => value === 'IGNORE' || value === '1'),
        v.boolean('AWS_ACXIOM_SQS_ACTIVE must be a boolean.'),
      ),
      AWS_ELASTICACHE_HOSTNAME: v.pipe(
        v.optional(v.string()),
        v.transform(input => (input === undefined ? '' : input)),
        v.string('AWS_ELASTICACHE_HOSTNAME must be a string.'),
      ),
      AWS_ELASTICACHE_NAME: v.pipe(
        v.optional(v.string()),
        v.transform(input => (input === undefined ? '' : input)),
        v.string('AWS_ELASTICACHE_NAME must be a string.'),
      ),
      AWS_ELASTICACHE_USERNAME: v.pipe(
        v.optional(v.string()),
        v.transform(input => (input === undefined ? '' : input)),
        v.string('AWS_ELASTICACHE_USERNAME must be a string.'),
      ),
      AWS_VERCEL_ROLE_ARN: v.pipe(
        v.optional(v.string()),
        v.transform(input => (input === undefined ? '' : input)),
        v.string('AWS_VERCEL_ROLE_ARN must be a string.'),
      ),
      CRON_SECRET: v.fallback(v.string(), ''),
      DATADOG_API_KEY: v.fallback(v.string(), ''),
      DD_APP_KEY: v.fallback(v.string(), ''),
      EDGE_CONFIG_ID: v.fallback(v.string(), ''),
      EDGE_CONFIG_SECRET: v.fallback(v.string(), ''),
      INTERNAL_SUPERGRAPH_URL: v.pipe(
        v.string(),
        v.url('INTERNAL_SUPERGRAPH_URL must be a valid URL'),
      ),
      ENABLE_LOCAL_REDIS: v.pipe(
        v.fallback(v.string(), 'false'),
        v.transform(value => value === 'true'),
      ),
      LOG_LEVEL: v.fallback(
        v.picklist(
          ['fatal', 'error', 'warn', 'info', 'debug', 'trace', 'silent'],
          'LOG_LEVEL must be one of: fatal, error, warn, info, debug, trace, silent',
        ),
        'info',
      ),
      NODE_ENV: v.picklist(
        ['production', 'development', 'test'],
        'NODE_ENV must be one of: production, development, test',
      ),
      SECTION_SPECIFIC_ML_CLOUDFRONT_URL: v.pipe(
        v.string(),
        v.url('SECTION_SPECIFIC_ML_CLOUDFRONT_URL must be a valid URL'),
      ),
      TARGET_CONFIG: v.fallback(v.picklist(['dev', 'stage', 'prod']), 'dev'),
      SPINWHEEL_BASE_URL: v.pipe(
        v.optional(v.pipe(v.string(), v.url('SPINWHEEL_BASE_URL must be a valid URL'))),
        v.transform(input => (input === undefined ? '' : input)),
        v.string('SPINWHEEL_BASE_URL must be a string'),
      ),
      SPINWHEEL_SECRET_KEY: v.pipe(
        v.optional(v.string()),
        v.transform(input => (input === undefined ? '' : input)),
        v.string('SPINWHEEL_SECRET_KEY must be a string'),
      ),
      APPROVAL_ODDS_SESSION_SECRET: v.pipe(
        v.optional(v.string()),
        v.transform(input => (input === undefined ? '' : input)),
        // TODO: add check for length >= 32 on tech check
        v.string('APPROVAL_ODDS_SESSION_SECRET must be a string'),
      ),
      TRANSUNION_ENDPOINT: v.pipe(v.string(), v.url('TRANSUNION_ENDPOINT must be a valid URL')),
      VERCEL_API_TOKEN: v.fallback(v.string(), ''),
      OPENAI_API_KEY_WP_PREVIEWS: v.fallback(v.string(), ''),
    },
  },
  values: {
    NEXT_PUBLIC_ABSMARTLY_URL,
    NEXT_PUBLIC_SUPERGRAPH_URL,

    ACXIOM_CLIENT_ID: process.env.ACXIOM_CLIENT_ID,
    ACXIOM_CLIENT_SECRET: process.env.ACXIOM_CLIENT_SECRET,
    ASSET_PREFIX: process.env.ASSET_PREFIX,
    AWS_ACXIOM_SQS_QUEUE_URL: process.env.AWS_ACXIOM_SQS_QUEUE_URL,
    AWS_ACXIOM_SQS_ACTIVE: process.env.AWS_ACXIOM_SQS_ACTIVE,
    AWS_ELASTICACHE_HOSTNAME: process.env.AWS_ELASTICACHE_HOSTNAME,
    AWS_ELASTICACHE_NAME: process.env.AWS_ELASTICACHE_NAME,
    AWS_ELASTICACHE_USERNAME: process.env.AWS_ELASTICACHE_USERNAME,
    AWS_VERCEL_ROLE_ARN: process.env.AWS_VERCEL_ROLE_ARN,
    CRON_SECRET: process.env.CRON_SECRET,
    DATADOG_API_KEY: process.env.DATADOG_API_KEY,
    DD_APP_KEY: process.env.DD_APP_KEY,
    EDGE_CONFIG_ID: process.env.EDGE_CONFIG_ID,
    EDGE_CONFIG_SECRET: process.env.EDGE_CONFIG_SECRET,
    ENABLE_LOCAL_REDIS: process.env.ENABLE_LOCAL_REDIS,
    INTERNAL_SUPERGRAPH_URL: process.env.INTERNAL_SUPERGRAPH_URL,
    LOG_LEVEL: process.env.LOG_LEVEL,
    NODE_ENV: process.env.NODE_ENV,
    OPENAI_API_KEY_WP_PREVIEWS: process.env.OPENAI_API_KEY_WP_PREVIEWS,
    SECTION_SPECIFIC_ML_CLOUDFRONT_URL: process.env.SECTION_SPECIFIC_ML_CLOUDFRONT_URL,
    TARGET_CONFIG: process.env.TARGET_CONFIG,
    SPINWHEEL_BASE_URL: process.env.SPINWHEEL_BASE_URL,
    SPINWHEEL_SECRET_KEY: process.env.SPINWHEEL_SECRET_KEY,
    APPROVAL_ODDS_SESSION_SECRET: process.env.APPROVAL_ODDS_SESSION_SECRET,
    TRANSUNION_ENDPOINT: process.env.TRANSUNION_ENDPOINT,
    VERCEL_API_TOKEN: process.env.VERCEL_API_TOKEN,

    NEXT_PUBLIC_ABSMARTLY_API_KEY: process.env.NEXT_PUBLIC_ABSMARTLY_API_KEY,
    NEXT_PUBLIC_DD_CLIENT_TOKEN: process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN,
    NEXT_PUBLIC_DEPLOYABLE_ENV: process.env.NEXT_PUBLIC_DEPLOYABLE_ENV,
    NEXT_PUBLIC_NW_URL: process.env.NEXT_PUBLIC_NW_URL,
    NEXT_PUBLIC_SEGMENT_WRITE_KEY: process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY,
    NEXT_PUBLIC_VERCEL_DEPLOYMENT_ID: process.env.NEXT_PUBLIC_VERCEL_DEPLOYMENT_ID,
    NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
  },
});
